/* global angular */
export class QuizService {
    constructor() {
    }

    enrichProgrammeWithPatientProgrammeInformation(programme, patientProgrammeInformation) {
        this._lockModulesByProgress(programme, patientProgrammeInformation.progress);
        this._calculateCompletionAndAdjustLocksByQuizzes(programme, patientProgrammeInformation);
    }

    /**
     * @private
     */
    _lockModulesByProgress(programme, progress) {
        const progressValue = progress || 0;
        const progressToModulesUnlockMap = programme.fields.settings.progressToModulesUnlockMap;

        //purge the programme from last mappings
        programme.fields.modules.forEach(module => {
            delete module.isBlocked;
        });

        if (!angular.isObject(progressToModulesUnlockMap)) {
            return;
        }

        let unlockedModulesSet = this._getUnlockedModuleIdsSet(progressToModulesUnlockMap, progressValue);
        programme.fields.modules.forEach(module => {
            module.isBlocked = !unlockedModulesSet.has(module.sys.id);
        });
    }

    /**
     * @private
     */
    _getUnlockedModuleIdsSet(progressToModulesUnlockMap, progressValue) {
        return Object.keys(progressToModulesUnlockMap)
            .filter(key => key <= progressValue)
            .reduce((unlockedModules, key) => {
                progressToModulesUnlockMap[key].forEach(module => unlockedModules.add(module));
                return unlockedModules;
            }, new Set());
    }

    /**
     * @private
     */
    _calculateCompletionAndAdjustLocksByQuizzes(programme, patientProgrammeInformation) {
        const completedQuizIds = new Set(patientProgrammeInformation.achievementContentIds);
        const viewedContentIds = new Set(patientProgrammeInformation.viewedContentIds);
        const quizBlocksProgress = programme.fields.settings.quizBlocksProgress;
        const submissionRequired = this.isSubmissionRequired(programme);

        let progressBlocked = false;

        programme.fields.modules.forEach(module => {

            module.isBlocked = progressBlocked || module.isBlocked;
            module.completedModuleStepsCount = 0;

            if (!QuizService._hasRlos(module)) {
                return;
            }

            module.fields.rlos.forEach(rlo => {
                rlo.isBlocked = progressBlocked;
                if (rlo.sys.contentType.sys.id === 'quiz') {
                    rlo.isCompleted = completedQuizIds.has(rlo.sys.id);
                    rlo.quizBlocksNextEntry = !rlo.isCompleted && (quizBlocksProgress || submissionRequired);
                    if (!rlo.isCompleted) {
                        progressBlocked = quizBlocksProgress;
                    }
                } else {
                    rlo.isCompleted = viewedContentIds.has(rlo.sys.id);
                }
            });

            module.completedModuleStepsCount = module.fields.rlos.reduce((count, rlo) => rlo.isCompleted ? count + 1 : count, 0);
        });
    }

    /**
     * Whether the course requires every quiz to be submitted once (regardless of correctness) before continuing.
     * Dedicated Contentful field on the programme; a missing value means off.
     *
     * @return {boolean}
     */
    // noinspection JSMethodCanBeStatic - Used as service method by controllers
    isSubmissionRequired(programme) {
        return angular.isObject(programme) &&
            angular.isObject(programme.fields) &&
            programme.fields.quizSubmissionRequired === true;
    }

    /**
     * Whether the correct answers are shown after a wrong or partially correct answer.
     * Dedicated Contentful field on the programme; a missing value means off.
     *
     * @return {boolean}
     */
    // noinspection JSMethodCanBeStatic - Used as service method by controllers
    isShowCorrectAnswers(programme) {
        return angular.isObject(programme) &&
            angular.isObject(programme.fields) &&
            programme.fields.quizShowCorrectAnswers === true;
    }

    /**
     * Remembers that the quiz has been submitted, which releases its Next button.
     */
    markQuizSubmitted(programme, quizId) {
        if (!angular.isObject(programme) || !angular.isObject(programme.fields) || !angular.isArray(programme.fields.modules)) {
            return;
        }

        programme.fields.modules.forEach(module => {
            if (QuizService._hasRlos(module)) {
                module.fields.rlos.forEach(rlo => {
                    if (rlo.sys.id === quizId) {
                        rlo.quizBlocksNextEntry = false;
                    }
                });
            }
        });
    }

    /**
     * Whether the quiz with the given id is already recorded as completed in the (enriched) programme.
     *
     * @return {boolean}
     */
    // noinspection JSMethodCanBeStatic - Used as service method by controllers
    isQuizCompleted(programme, quizId) {
        if (!angular.isObject(programme) || !angular.isObject(programme.fields) || !angular.isArray(programme.fields.modules)) {
            return false;
        }

        return programme.fields.modules.some(module =>
            QuizService._hasRlos(module) &&
            module.fields.rlos.some(rlo => rlo.sys.id === quizId && rlo.isCompleted === true));
    }

    /**
     * @return {boolean}
     */
    static _hasRlos(module) {
        return angular.isObject(module) &&
            angular.isObject(module.fields) &&
            angular.isArray(module.fields.rlos) &&
            module.fields.rlos.length > 0;
    }
}